import { useEffect, useRef } from "react";

/**
 * Cloudflare Turnstile widget.
 *
 * Renders nothing when VITE_TURNSTILE_SITE_KEY is unset, which is how local
 * development and preview branches work without a key. The server mirrors
 * this: `functions/_shared/forms.js` skips verification when its own secret is
 * missing, so an unconfigured production deploy accepts submissions with only
 * the honeypot protecting it. Set both keys together.
 */

interface TurnstileApi {
  render: (
    el: HTMLElement,
    options: {
      sitekey: string;
      action: TurnstileAction;
      callback: (token: string) => void;
      "expired-callback"?: () => void;
      "error-callback"?: () => void;
    }
  ) => string;
  remove: (widgetId: string) => void;
}

/**
 * One action per protected endpoint. The server checks the action that comes
 * back from siteverify (`TURNSTILE_ACTIONS` in `functions/_shared/forms.js`),
 * so a token solved for one form cannot be replayed against another.
 */
export type TurnstileAction = "newsletter" | "lead";

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let scriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error("Turnstile script failed to load"));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

interface TurnstileProps {
  action: TurnstileAction;
  onToken: (token: string) => void;
  /**
   * Change this to discard the current token and issue a fresh one. Tokens are
   * single-use, so a failed submit must reset the widget or the retry is
   * rejected as a replay.
   */
  resetSignal?: number;
}

export function Turnstile({ action, onToken, resetSignal = 0 }: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (!siteKey || !containerRef.current) return;

    const container = containerRef.current;
    let widgetId: string | undefined;
    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !window.turnstile) return;
        widgetId = window.turnstile.render(container, {
          sitekey: siteKey,
          action,
          callback: (token) => onTokenRef.current(token),
          "expired-callback": () => onTokenRef.current(""),
          "error-callback": () => onTokenRef.current(""),
        });
      })
      .catch((err) => {
        // A blocked or failed challenge script must not strand the form. The
        // server rejects a missing token when it has a secret configured, so
        // this surfaces as a normal submit error rather than a dead button.
        console.error(err.message);
      });

    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [siteKey, action, resetSignal]);

  if (!siteKey) return null;

  return <div ref={containerRef} className="mt-1" />;
}
